import jwt from 'jsonwebtoken';
import { NextApiRequest } from 'next';
import { type JWT, getToken as nextAuthGetToken } from 'next-auth/jwt';

import { logger } from 'logger';

const refreshAccessToken = async (token: JWT): Promise<JWT> => {
    try {
        const clientId = process.env.KEYCLOAK_ID;
        const clientSecret = process.env.KEYCLOAK_SECRET;
        const issuer = process.env.KEYCLOAK_ISSUER;
        const refreshToken = token.refreshToken as string | undefined;

        if (!clientId || !clientSecret || !issuer || !refreshToken) {
            logger.error(
                {
                    hasClientId: Boolean(clientId),
                    hasClientSecret: Boolean(clientSecret),
                    hasIssuer: Boolean(issuer),
                    hasRefreshToken: Boolean(refreshToken),
                },
                'Missing required values for token refresh',
            );
            return {
                ...token,
                tokenError: 'RefreshAccessTokenError',
            };
        }

        const details: Record<string, string> = {
            client_id: clientId,
            client_secret: clientSecret,
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
        };
        const formBody: string[] = [];
        Object.entries(details).forEach(([key, value]) => {
            if (value == null || value === '') return;
            formBody.push(
                encodeURIComponent(key) + '=' + encodeURIComponent(value),
            );
        });
        const formData = formBody.join('&');
        const url = `${issuer}/protocol/openid-connect/token`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type':
                    'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: formData,
        });
        const refreshedTokens = await response.json();

        if (!response.ok) {
            logger.error(
                {
                    status: response.status,
                    statusText: response.statusText,
                    responseBody: refreshedTokens,
                },
                'Error response from keycloak token refresh',
            );
            return {
                ...token,
                tokenError: 'RefreshAccessTokenError',
            };
        }

        logger.info(response, 'Got refresh token from keycloak');

        const tokenWithoutError = { ...token };
        delete tokenWithoutError.tokenError;

        return {
            ...tokenWithoutError,
            accessToken: refreshedTokens.access_token,
            refreshToken: refreshedTokens.refresh_token ?? token.refreshToken,
        };
    } catch (error) {
        logger.error(error, 'Error refreshing token from keycloak');

        return {
            ...token,
            tokenError: 'RefreshAccessTokenError',
        };
    }
};

export const getToken: ({
    req,
}: {
    req: NextApiRequest;
}) => Promise<JWT | null> = async ({ req }) => {
    const token = await nextAuthGetToken({ req });

    return token ? await refreshTokenIfNeeded(token) : null;
};

export const refreshTokenIfNeeded = async (token: JWT): Promise<JWT> => {
    const decodedAccessToken = jwt.decode(token.accessToken as string) as {
        exp?: number;
    } | null;

    if (
        decodedAccessToken?.exp != null &&
        Date.now() > (decodedAccessToken.exp - 15) * 1000
    ) {
        return refreshAccessToken(token);
    }

    return token;
};
